import { useEffect, useState } from "react";
import { useList, useCreate, useUpdate } from "@/hooks/crud";
import { TextAreaField, SelectField, TextField } from "@/components/Fields";
import { StatusBadge } from "@/components/Badges";
import { ErrorBanner } from "@/components/Feedback";
import { CONCEPT_STATUSES } from "@/lib/enums";
import type { TrackConcept } from "@/types/database";

export function ConceptPanel({ trackId }: { trackId: number }) {
  const { data } = useList("track_concepts", `track_id=eq.${trackId}`);
  const concept = (data?.rows?.[0] ?? null) as TrackConcept | null;

  const create = useCreate("track_concepts");
  const update = useUpdate("track_concepts");

  const [brief, setBrief] = useState("");
  const [descriptors, setDescriptors] = useState("");
  const [sourceNote, setSourceNote] = useState("");
  const [status, setStatus] = useState("DRAFT");

  useEffect(() => {
    setBrief(concept?.brief ?? "");
    setDescriptors(concept?.emotional_descriptors ?? "");
    setSourceNote(concept?.source_note ?? "");
    setStatus(concept?.status ?? "DRAFT");
  }, [concept]);

  const mutation = concept ? update : create;

  async function save() {
    const body = {
      track_id: trackId,
      brief: brief.trim() || null,
      emotional_descriptors: descriptors.trim() || null,
      source_note: sourceNote.trim() || null,
      status,
    };
    if (concept) await update.mutateAsync({ id: concept.id, body });
    else await create.mutateAsync(body);
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>
          Concept — Emotive Programming
          {concept && <StatusBadge status={concept.status} />}
        </h3>
      </div>
      <div className="panel-body">
        {mutation.error && <ErrorBanner error={mutation.error} />}
        <TextAreaField
          label="Brief (non-musical description)"
          value={brief}
          onChange={setBrief}
          rows={5}
          placeholder="Describe the track in human, non-musical vocabulary…"
        />
        <TextAreaField
          label="Emotional descriptors"
          value={descriptors}
          onChange={setDescriptors}
          rows={2}
        />
        <div className="field-row">
          <SelectField
            label="Status"
            value={status}
            onChange={setStatus}
            options={CONCEPT_STATUSES}
          />
          <TextField
            label="Source note"
            value={sourceNote}
            onChange={setSourceNote}
            placeholder="provenance"
          />
        </div>
        <div className="row-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={save}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Saving…" : concept ? "Save Concept" : "Create Concept"}
          </button>
        </div>
      </div>
    </div>
  );
}
